/**
 * Add / edit one force-update policy. Structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this app uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * page-with-back-button + FormGroup field layout) — the same shape as `../catalogue/brands/New.tsx`.
 *
 * App and platform are editable on both pages: the API moves a policy to another pair, and refuses
 * (409) a pair another policy already holds — shown on the Platform field, nothing saved. Format is
 * checked here; whether min is above current is the API's comparison, answered as a field error.
 */
import { useState } from "react";
import { useLoaderData, useNavigate, type LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import { PiDeviceMobileDuotone as AppIcon, PiTagDuotone as VersionIcon, PiShieldCheckDuotone as PolicyIcon } from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import { PageHeader, ErrorSurface, problemTitle, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { StatusSwitch } from "../../components/status-switch";
import { SectionTitle } from "../../components/section-title";
import { ApiError } from "../../lib/api";
import { formCard, formCardFooter, formSections } from "../../lib/form-layout";
import { useGoBack } from "../../lib/navigation";
import {
  APP_LABEL,
  PLATFORM_LABEL,
  createAppVersion,
  listAppVersions,
  policyName,
  updateAppVersion,
  type AppVersionPolicy,
} from "../../lib/services/appVersions";

export async function newAppVersionLoader() {
  return { policy: null as AppVersionPolicy | null };
}

/** `/app-versions/customer-android`. At most four policies, so the list is the lookup. */
export async function editAppVersionLoader({ params }: LoaderFunctionArgs) {
  const policy = (await listAppVersions()).find((p) => `${p.app}-${p.platform}` === params.key);
  if (!policy) throw new ApiError(404, { title: "No such app version policy", status: 404 });
  return { policy: policy as AppVersionPolicy | null };
}

// The API's own format rule (AppVersion.TryParse): digits, no leading zeros, no suffix.
const VERSION = /^(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})\.(0|[1-9]\d{0,8})$/;
const version = (label: string) => z.string().trim().min(1, `${label} is required`).regex(VERSION, "Use MAJOR.MINOR.PATCH, e.g. 1.2.0");

const policySchema = z.object({
  app: z.string().min(1, "Choose an app"),
  platform: z.string().min(1, "Choose a platform"),
  minVersion: version("Min version"),
  currentVersion: version("Current version"),
  forceUpdate: z.boolean(),
});
type PolicyFormValues = z.infer<typeof policySchema>;
type FieldName = keyof PolicyFormValues;

/** The API's 400 `errors` (keyed by body property) or 409, shown on the field it is about. */
function serverFieldErrors(err: unknown, values: PolicyFormValues): Partial<Record<FieldName, string>> {
  if (!(err instanceof ApiError)) return {};
  if (err.status === 409) return { platform: `${policyName(values)} already has a version policy. Edit that one instead.` };
  const errors = (err.raw as { errors?: Record<string, string[]> } | null)?.errors ?? {};
  return Object.fromEntries(Object.entries(errors).map(([field, messages]) => [field, messages[0]]));
}

export default function AppVersionFormPage() {
  const { policy } = useLoaderData() as Awaited<ReturnType<typeof editAppVersionLoader>>;
  const navigate = useNavigate();
  const goBack = useGoBack("/app-versions");
  const [error, setError] = useState<unknown>(null);
  const [serverErrors, setServerErrors] = useState<Partial<Record<FieldName, string>>>({});

  const title = policy ? `Edit ${policyName(policy)}` : "Add App Version";
  const defaultValues: PolicyFormValues = policy
    ? { app: policy.app, platform: policy.platform, minVersion: policy.minVersion, currentVersion: policy.currentVersion, forceUpdate: policy.forceUpdate }
    : { app: "", platform: "", minVersion: "", currentVersion: "", forceUpdate: true };

  async function submit(values: PolicyFormValues) {
    setError(null);
    setServerErrors({});
    const input = {
      app: values.app,
      platform: values.platform,
      minVersion: values.minVersion.trim(),
      currentVersion: values.currentVersion.trim(),
      forceUpdate: values.forceUpdate,
    };
    try {
      if (policy) await updateAppVersion(policy.id, input);
      else await createAppVersion(input);
      toast.success(`${policyName(input)} policy ${policy ? "updated" : "added"}`);
      // To the list, not back: an edit may have moved the policy off this page's own URL.
      navigate("/app-versions");
    } catch (err) {
      setError(err);
      setServerErrors(serverFieldErrors(err, values));
      toast.error(problemTitle(err, policy ? "Could not update this policy" : "Could not add this policy"));
    }
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader title={title} breadcrumb={[{ name: "Home", href: "/" }, { name: "Administration · App Versions", href: "/app-versions" }, { name: title }]} />

      <Form<PolicyFormValues> key={policy?.id ?? "new"} validationSchema={policySchema} useFormProps={{ defaultValues }} className={formCard} onSubmit={submit}>
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              {error ? <ErrorSurface error={error} action={policy ? `update ${policyName(policy)}` : "add this policy"} /> : null}

              <FormGroup title={<SectionTitle icon={AppIcon}>Application</SectionTitle>} description="Which mobile app, on which platform. One policy per pair.">
                <SelectField label="App" required error={errors.app?.message ?? serverErrors.app} {...register("app")}>
                  <option value="">Choose an app…</option>
                  {Object.entries(APP_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </SelectField>
                <SelectField label="Platform" required error={errors.platform?.message ?? serverErrors.platform} {...register("platform")}>
                  <option value="">Choose a platform…</option>
                  {Object.entries(PLATFORM_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </SelectField>
              </FormGroup>

              <FormGroup title={<SectionTitle icon={VersionIcon}>Version details</SectionTitle>} description="MAJOR.MINOR.PATCH, exactly as published to the store.">
                <Field
                  label="Min version"
                  required
                  placeholder="1.2.0"
                  hint="The oldest release allowed while force update is enabled."
                  error={errors.minVersion?.message ?? serverErrors.minVersion}
                  {...register("minVersion")}
                />
                <Field
                  label="Current version"
                  required
                  placeholder="1.5.0"
                  hint="The newest published release."
                  error={errors.currentVersion?.message ?? serverErrors.currentVersion}
                  {...register("currentVersion")}
                />
              </FormGroup>

              <FormGroup
                title={<SectionTitle icon={PolicyIcon}>Update policy</SectionTitle>}
                description="Users below the min version are blocked only while force update is enabled. Below the current version, an update is only offered."
              >
                <StatusSwitch
                  onLabel="Enabled"
                  offLabel="Disabled"
                  aria-label="Force update"
                  activeText="Force update: a release below the min version cannot be used until it is updated."
                  inactiveText="Force update: nobody is blocked, whatever release they run."
                  {...register("forceUpdate")}
                />
              </FormGroup>
            </div>

            <FormFooter
              className={formCardFooter}
              submitBtnText={isSubmitting ? "Saving…" : "Save changes"}
              isLoading={isSubmitting}
              altBtnText="Cancel"
              handleAltBtn={goBack}
            />
          </>
        )}
      </Form>
    </div>
  );
}
